import { useFormContext, useFieldArray, Controller } from 'react-hook-form'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { Checkbox, Input } from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'

import { VendorProfileFormType } from '../type'

import InputField from './InputField'

import SectionWrapper from './SectionWrapper'
import SVGLoader from '@src/components/svg/SVGLoader'
import ErrorMessage from './ErrorMessage'

const ContactInfoForm = () => {
	const {
		control,
		getValues,
		setValue,
		clearErrors,
		formState: { errors },
	} = useFormContext<VendorProfileFormType>()
	const {
		fields: contactsFields,
		append,
		remove,
	} = useFieldArray<VendorProfileFormType, 'vendorContacts', 'id'>({
		name: 'vendorContacts',
		control,
	})

	const handleClickAddContact = () => {
		append({ name: '', position: '', email: '', phone: '' }, { shouldFocus: false })
	}

	const handleRemoveVendorContact = (idx: number) => {
		remove(idx)
	}

	const handleClickCopyCeoInfoToFillContactInfo = (e: CheckboxChangeEvent) => {
		if (e.target.checked) {
			const ceoInfo = getValues(['ceoName', 'phone', 'email'])
			setValue('vendorContacts.0.name', ceoInfo[0], { shouldDirty: true })
			setValue('vendorContacts.0.phone', ceoInfo[1], { shouldDirty: true })
			setValue('vendorContacts.0.email', ceoInfo[2], { shouldDirty: true })
			setValue('vendorContacts.0.position', '대표', { shouldDirty: true })
			clearErrors('vendorContacts.0.position')
			if (ceoInfo[0]) clearErrors('vendorContacts.0.name')
			if (ceoInfo[1]) clearErrors('vendorContacts.0.phone')
			if (ceoInfo[2]) clearErrors('vendorContacts.0.email')
		}
	}

	return (
		<SectionWrapper
			title={
				<SectionTitleBlock>
					<SectionTitle>담당자 정보</SectionTitle>
					<AddButton onClick={handleClickAddContact}>
						<SVGLoader name="plus" />
						담당자 추가
					</AddButton>
				</SectionTitleBlock>
			}
		>
			<Section>
				<CopyCheckBox onChange={handleClickCopyCeoInfoToFillContactInfo}>
					대표자 정보와 동일
				</CopyCheckBox>
				{contactsFields.map((contactInfo, idx) => (
					<ContactInfoBlock key={`contact-info-${contactInfo.id}`}>
						<InputField label="담당자 명">
							<Controller
								control={control}
								name={`vendorContacts.${idx}.name` as const}
								render={({ field }) => {
									return <Input {...field} placeholder="담당자명을 입력해주세요." />
								}}
							/>
							<ErrorMessage errorMessage={errors.vendorContacts?.[idx]?.name?.message} />
						</InputField>
						<InputField label="직책">
							<Controller
								control={control}
								name={`vendorContacts.${idx}.position` as const}
								render={({ field }) => {
									return <Input {...field} placeholder="직책을 입력해주세요." />
								}}
							/>
							<ErrorMessage errorMessage={errors.vendorContacts?.[idx]?.position?.message} />
						</InputField>
						<InputField label="담당자 이메일">
							<Controller
								control={control}
								name={`vendorContacts.${idx}.email` as const}
								render={({ field }) => {
									return <Input {...field} placeholder="담당자 이메일을 입력해주세요." />
								}}
							/>
							<ErrorMessage errorMessage={errors.vendorContacts?.[idx]?.email?.message} />
						</InputField>
						<InputField label="담당자 연락처">
							<Controller
								control={control}
								name={`vendorContacts.${idx}.phone` as const}
								render={({ field }) => {
									return <Input {...field} placeholder="담당자 연락처를 입력해주세요." />
								}}
							/>
							<ErrorMessage errorMessage={errors.vendorContacts?.[idx]?.phone?.message} />
						</InputField>
						{idx !== 0 ? (
							<RemoveButton type="button" onClick={() => handleRemoveVendorContact(idx)}>
								삭제
							</RemoveButton>
						) : null}
					</ContactInfoBlock>
				))}
			</Section>
		</SectionWrapper>
	)
}

const SectionTitleBlock = styled.div`
	display: flex;
	align-items: center;
`

const SectionTitle = styled.h3`
	color: #000;
	font-family: Pretendard;
	font-size: 20px;
	font-style: normal;
	font-weight: 700;
	line-height: normal;
	letter-spacing: 0.2px;
	margin-right: 2.2rem;
`

const Section = styled.div`
	display: flex;
	flex-direction: column;
`

const ContactInfoBlock = styled.div`
	position: relative;
	border: 1px solid #e7e7ed;
	padding: 2rem;
	border-radius: 0.5rem;

	& + & {
		margin-top: 1.7rem;
	}
`

const CopyCheckBox = styled(Checkbox)`
	color: #000;
	font-family: Pretendard;
	font-size: 1.6rem;
	font-style: normal;
	font-weight: 600;
	line-height: normal;
	letter-spacing: 0.2px;
	margin-top: -0.7rem;
	margin-bottom: 2.7rem;
	.ant-checkbox-checked .ant-checkbox-inner {
		background-color: #000;
		border-color: #000;
	}
`

const buttonStyle = css`
	font-size: 1.4rem;
	line-height: 2.4rem;
	font-weight: 600;
	text-align: center;
	height: 4rem;
	padding: 0 1.6rem;
	background-color: #fff;
	border: 1px solid #e7e7ed;
	color: #363644;
	cursor: pointer;
`

const AddButton = styled.button`
	${buttonStyle}
	display: flex;
	align-items: center;

	& > svg {
		width: 1.1rem;
		height: 1.1rem;
		margin-right: 0.6rem;
	}
`

const RemoveButton = styled.button`
	${buttonStyle}
	position: absolute;
	right: 2rem;
	bottom: 2rem;
`

export default ContactInfoForm
